import React, { useEffect, useState } from "react";
import "./ChatStyles.css";
import SidebarLayout from "../../common/sidebar/SidebarLayout";
import ChatListing from "./ChatListing";
import ChatTexting from "./ChatTexting";
import { getFullName, notify } from "../../../util/helpers";
import NoSelectedText from "./NoSelectedText";
import { useDispatch, useSelector } from "react-redux";
import {
  getChatEnabledPatientsLogic,
  getChatHistoryLogic,
  getSinglePatientChat,
  getUnreadMessagesLogic,
  markAsReadLogic,
} from "../../../store/features/chat/chatSlice";
import AgoraClientComponent, {
  chatStructureHelper,
  sendMessage,
} from "./AgoraMethods";
import { isEmptyObject } from "jquery";
import IncomingMessage from "../../../assets/sounds/IncomingMessage.mp3";
import DummyClient from "./DummyClient";
import DualLoginModal from "./DualLoginModal";
import { Helmet } from "react-helmet";
import PatientLoader from "../../common/PatientLoader";
import { useNavigate } from "react-router-dom";

const Chat = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [chatData, setChatData] = useState(null);
  const [remoteShow, setRemoteShow] = useState(false);
  const [chatPatients, setChatPatients] = useState([]);
  const [singleChatLoading, setSingleChatLoading] = useState(false);
  const [chatEnabledPatients, setChatEnabledPatients] = useState([]);
  const [latestMessage, setLatestMessage] = useState(false);
  const [inChatMessage, setInChatMessage] = useState(null);
  const [clinicianChatMessage, setClinicianChatMessage] = useState(null);

  const { user, selectedClinic, selectedProvider } = useSelector(
    (state) => state.app
  );
  const {
    chatHistory,
    chatLoading,
    chatError,
    chatEnabledPatientsData,
    chatEnabledPatientsLoading,
    chatEnabledPatientsErrors,
  } = useSelector((state) => state.chat);

  const clinicianId = selectedProvider ? selectedProvider : user?.id;
  const clinicianName = getFullName(user);
  const clinicAdmin = selectedClinic?.clinic_admin;

  useEffect(() => {
    setChatData(null); //* Without this, if a chat was selected, it remained on screen even when loader was spinning to get new data.
    dispatch(
      getChatHistoryLogic({
        page_size: "9999999",
        clinic_id: selectedClinic?.clinic?.id,
        clinician_id: clinicianId,
      })
    );
    dispatch(
      getChatEnabledPatientsLogic({
        clinic_id: selectedClinic?.clinic?.id,
        clinician_id: clinicianId,
      })
    );
    dispatch(getUnreadMessagesLogic());
    sessionStorage.removeItem("openChatID");

    return () => {
      setChatData(null);
      sessionStorage.removeItem("openChatID");
    };
  }, []);

  useEffect(() => {
    if (chatError) navigate("/dashboard/home");
  }, [chatError]);

  const handlePeerMessage = (message, peerID) => {
    //* If the chat is currently open
    let openChatID = sessionStorage.getItem("openChatID");
    if (!openChatID) {
      openChatID = chatData?.patient_id;
      sessionStorage.setItem("openChatID", chatData?.patient_id);
    }

    if (openChatID === peerID) {
      setInChatMessage(message);
      //* Now update the sidebar. This is done regardless of whether the chat is open or not.
      setLatestMessage({
        message: message,
        peerID: peerID,
        updateBadge: false,
      });
    } else {
      //* Update the sidebar.
      setLatestMessage({
        message: message,
        peerID: peerID,
        updateBadge: true,
      });
    }
  };

  useEffect(() => {
    if (chatHistory) {
      if (chatHistory?.results?.length > 0) {
        setChatPatients(chatHistory?.results);
      }
    }
  }, [chatLoading, chatHistory]);

  useEffect(() => {
    if (!chatEnabledPatientsLoading && !chatEnabledPatientsErrors) {
      setChatData(null);
      setChatEnabledPatients(chatEnabledPatientsData?.results);
    }
  }, [
    chatEnabledPatientsLoading,
    chatEnabledPatientsData,
    chatEnabledPatientsErrors,
  ]);

  const setSinglePatientChatData = (
    singleChatData = null,
    patient_id = null
  ) => {
    setChatData(null);
    setSingleChatLoading(true);
    let patientID = patient_id;
    if (singleChatData) {
      patientID = singleChatData?.patient_id;
    }

    //*Getting all chats for this patient
    getSinglePatientChat({
      to: patientID,
      sender:patientID,
      page_size: "9999999",
    })
      .then((res) => {
        if (res.status === 200) {
          let formattedData = {};
          const messages = res.data?.results;

          if (messages?.length > 0) {
            messages.forEach((item) => {
              chatStructureHelper(
                item,
                formattedData,
                clinicianId,
                clinicianName,
                clinicAdmin,
              );
            });
          }
          sessionStorage.removeItem("openChatID");
          sessionStorage.setItem("openChatID", patientID.toString());

          if (isEmptyObject(formattedData)) {
            setChatData(singleChatData);
            const tempChatPatients = [singleChatData, ...chatPatients];
            setChatPatients(tempChatPatients);
          } else {
            setChatData(formattedData[patientID]);
          }
        } else {
          notify(
            "Something went wrong while fetching messages, try again later!",
            "error"
          );
        }
      })
      .catch((e) => {
        console.log(e);
      });
    setSingleChatLoading(false);

    //*Marking the messages as read for this patient
    markAsReadLogic(patientID)
      .then((res) => {
        if (res.status === 200) {
          //* Removing unread message counter badge
          const chatTile = chatPatients.findIndex(
            (chat) => chat.patient_id === parseInt(patientID)
          );
          if (chatTile !== -1) {
            const temp = JSON.parse(JSON.stringify(chatPatients));
            temp[chatTile].count = 0;
            setChatPatients(temp);
          }
        }
      })
      .catch((e) => {
        console.log("Something went wrong while marking message as read", e);
      });
  };
  return (
    <SidebarLayout {...props}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Chat | Rose Health</title>
        <link rel="canonical" href={window.location.origin} />
      </Helmet>
      <div className="container-fluid bg-white py-3">
        <audio src={IncomingMessage} id="incomingMessageSound" muted autoPlay />
        {/* <DummyClient /> */}
        {/* <DualLoginModal modalIsOpen={remoteShow} setIsOpen={setRemoteShow} /> */}
        <AgoraClientComponent
          callback={handlePeerMessage}
          setRemoteShow={setRemoteShow}
        />
        <div className="container-fluid d-flex justify-content-start">
          <h3 className="primary-text-blue">Chats</h3>
        </div>
        {chatLoading ? (
          <PatientLoader />
        ) : (
          <div className=" container-fluid my-2 row">
            <ChatListing
              chatPatients={chatPatients}
              setChatData={setSinglePatientChatData}
              chatEnabledPatients={chatEnabledPatients}
              latestMessage={latestMessage}
              setChatPatients={setChatPatients}
            />

            {chatData ? (
              <ChatTexting
                name={chatData?.name}
                risk={chatData?.flag}
                patientID={chatData?.patient_id}
                providerData={{
                  name: clinicianName,
                  type: "Primary Provider",
                  clinicianId: clinicianId,
                }}
                closeChat={() => {
                  sessionStorage.removeItem("openChatID");
                  setChatData(null);
                }}
                chatHistory={chatData?.messages}
                singleChatLoading={singleChatLoading}
                sendMessage={sendMessage}
                inChatMessage={inChatMessage}
                setInChatMessage={setInChatMessage}
                chatData={chatData}
                clinicianChatMessage={clinicianChatMessage}
                setClinicianChatMessage={setClinicianChatMessage}
                setLatestMessage={setLatestMessage}
              />
            ) : (
              <>
                {chatLoading || singleChatLoading ? <></> : <NoSelectedText />}
              </>
            )}
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default Chat;
